import { useCallback, useState, type ReactNode } from "react";
import type { DOMNode, Element } from "html-react-parser";

/**
 * Code block with a copy button, as used by blog articles and the Knowledge
 * Center. Markup matches the `.code-block .copy-btn` styling those pages
 * carry — the button's icon and its copied state come from CSS.
 *
 * Lifted out of components/post-body.tsx so both can share one implementation.
 */
export function CopyableCode({ children, text }: { children: ReactNode; text: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            if (!navigator.clipboard) return;
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (err) {
            console.error("Failed to copy!", err);
        }
    }, [text]);

    return (
        <div className={`code-block content-copy${copied ? " clicked" : ""}`}>
            <button
                type="button"
                className="copy-btn"
                aria-label={copied ? "Copied" : "Copy code"}
                onClick={handleCopy}
            />
            {children}
        </div>
    );
}

/** Flattens a parsed node tree to plain text — what gets put on the clipboard. */
export function extractText(node: DOMNode | DOMNode[] | undefined): string {
    if (!node) return "";
    if (Array.isArray(node)) {
        return node.map((n) => extractText(n)).join("");
    }
    if ((node as any).type === "text") {
        return (node as any).data || "";
    }
    if ((node as Element).children) {
        return extractText((node as Element).children as DOMNode[]);
    }
    return "";
}

export default CopyableCode;
